import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, MessageCircle } from "lucide-react";

import useSEO from "../lib/useSEO";
import { fetchBlogPost, imageUrl } from "../lib/api";
import { BRAND, buildWhatsAppLink } from "../lib/constants";

/**
 * Tiny markdown-ish renderer for our blog posts.
 * Supports: ## heading, ### sub-heading, **bold**, lists ("- item"),
 * paragraphs separated by blank line. Keeps things small without an extra dep.
 */
function renderMarkdown(md) {
  const blocks = md.split(/\n{2,}/);
  return blocks.map((block, i) => {
    const trimmed = block.trim();
    if (trimmed.startsWith("## ")) {
      return <h2 key={i}>{trimmed.replace(/^##\s+/, "")}</h2>;
    }
    if (trimmed.startsWith("### ")) {
      return <h3 key={i}>{trimmed.replace(/^###\s+/, "")}</h3>;
    }
    if (trimmed.split("\n").every((l) => l.startsWith("- "))) {
      return (
        <ul key={i}>
          {trimmed
            .split("\n")
            .map((l, j) => (
              <li
                key={j}
                dangerouslySetInnerHTML={{ __html: inlineFormat(l.replace(/^-\s+/, "")) }}
              />
            ))}
        </ul>
      );
    }
    return (
      <p
        key={i}
        dangerouslySetInnerHTML={{ __html: inlineFormat(trimmed) }}
      />
    );
  });
}

function inlineFormat(s) {
  return s
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br/>");
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchBlogPost(slug)
      .then(setPost)
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useSEO(
    post
      ? {
          title: `${post.title} | ${BRAND.short} Blog`,
          description: post.excerpt,
          keywords: post.tags.join(", "),
          image: post.cover_image,
          canonical: window.location.origin + `/blog/${post.slug}`,
          jsonLd: {
            id: "blog-jsonld",
            data: {
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              headline: post.title,
              image: post.cover_image,
              author: { "@type": "Organization", name: BRAND.name },
              datePublished: post.published_at,
              keywords: post.tags.join(", "),
              description: post.excerpt,
            },
          },
        }
      : { title: "Artikel — " + BRAND.short }
  );

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <div className="h-12 w-3/4 bg-[#0a0a0a] animate-pulse mb-4" />
        <div className="h-4 w-1/3 bg-[#0a0a0a] animate-pulse mb-8" />
        <div className="aspect-[16/9] bg-[#0a0a0a] animate-pulse mb-8" />
        <div className="space-y-3">
          <div className="h-3 w-full bg-[#0a0a0a] animate-pulse" />
          <div className="h-3 w-5/6 bg-[#0a0a0a] animate-pulse" />
          <div className="h-3 w-4/6 bg-[#0a0a0a] animate-pulse" />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-32 text-center">
        <h1 className="font-display text-4xl uppercase text-white">
          Artikel tidak ditemukan
        </h1>
        <Link to="/blog" className="btn-ghost mt-6 inline-flex">
          <ArrowLeft className="w-4 h-4" /> Kembali ke Blog
        </Link>
      </div>
    );
  }

  return (
    <article
      data-testid="blog-detail-page"
      className="border-b border-[#1f1f1f]"
    >
      {/* Hero */}
      <section className="relative">
        <div className="aspect-[16/8] md:aspect-[16/6] overflow-hidden border-b border-[#1f1f1f]">
          <img
            src={imageUrl(post.cover_image)}
            alt={post.title}
            className="w-full h-full object-cover opacity-70"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
            <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[#CCFF00] font-bold mb-3">
              {post.tags.map((t) => (
                <span key={t}>#{t}</span>
              ))}
            </div>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] text-white">
              {post.title}
            </h1>
            <div className="mt-5 flex items-center gap-4 text-xs uppercase tracking-widest text-[#A3A3A3]">
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> {post.read_minutes} min baca
              </span>
              <span>—</span>
              <span>
                {new Date(post.published_at).toLocaleDateString("id-ID", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="prose-streetwear" data-testid="blog-content">
          {renderMarkdown(post.content)}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 border-t border-[#262626] pt-10 text-center">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-3">
            / Siap eksekusi?
          </p>
          <h3 className="font-display text-3xl sm:text-4xl uppercase text-white">
            Konsultasi Apparel Custom Sekarang
          </h3>
          <a
            href={buildWhatsAppLink()}
            target="_blank"
            rel="noreferrer"
            data-testid="blog-cta-wa"
            className="btn-primary mt-6 inline-flex"
          >
            <MessageCircle className="w-5 h-5" />
            Chat Admin
          </a>
        </div>

        <Link
          to="/blog"
          data-testid="blog-back-link"
          className="mt-12 inline-flex items-center gap-2 text-sm uppercase tracking-widest text-white hover:text-[#CCFF00]"
        >
          <ArrowLeft className="w-4 h-4" /> Semua Artikel
        </Link>
      </div>
    </article>
  );
}
